<template>
  <el-drawer
    v-model="visible"
    :close-on-click-modal="false"
    :modal="false"
    :append-to-body="false"
    :fullscreen="false"
    :size="drawerWidthPct"
    class="manage-jobs-drawer"
    modal-class="manage-jobs-drawer"
  >
    <template #header>
      <div class="manage-jobs-drawer__header">
        <h4 class="scripty">Auto-assign {{ itemTypeNamePlural.toLowerCase() }} with jobs&hellip;</h4>
      </div>
    </template>
    <template #default>
      <div>
        <p>
          Define <strong>Jobs</strong>
          so a {{ itemTypeNameSingular.toLowerCase() }} is automatically assigned to the person responsible
          for each job whenever the <strong>status</strong> changes. This replaces the ability to manually assign a
          {{ itemTypeNameSingular.toLowerCase() }} to a person.
          You can always turn jobs on or off inside of a {{ entityTypeNameSingular.toLowerCase() }}.
        </p>
        <p><br></p>
        <p>
          <strong>Example:</strong> A workflow has <strong>Fixer</strong> and <strong>Tester</strong> jobs.
          "Open" and "In Progress" {{ itemTypeNamePlural.toLowerCase() }} are assigned to the Fixer and 
          "Ready for Retest" {{ itemTypeNamePlural.toLowerCase() }} to the Tester.
        </p>
        <p><br></p>
        <div class="drag-container">
          <draggable
            :animation="180"
            :scroll-sensitivity="100"
            :scroll-speed="20"
            :force-fallback="true"
            :list="internalJobs"
            ghost-class="ghost-card"
            drag-class="drag-card"
            handle=".handle"
            class="drag-group"
            :item-key="positionKey"
          >
            <template #item="{ element: job, index }">
              <workflow-job-item
                :entity-type="entityType"
                :index="index"
                :job="job as IJob"
                :show-delete="!hasAssigneeMandate(job)"
                focus-on-create
                show-drag
                @delete-clicked="(index: number) => removeJob(index)"
              />
            </template>
          </draggable>
          <add-workflow-job-item
            v-if="internalJobs.length < maxJobsAllowed" 
            :entity-type="entityType"
            @clicked="addAdditionalJob"
          />
        </div>
        <div class="manage-jobs-drawer__footer">
          <dd-button
            :is-loading="isUpdatingJobs"
            :color="btnColor" 
            size="large"
            @click="updateJobs()"
          >
            Map these jobs to statuses&hellip;
          </dd-button>
        </div>
      </div>
    </template>
  </el-drawer>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import Draggable from 'vuedraggable'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import {
  IWorkflowsInputModifyJobs,
  IWorkflowsInputModifyJobsJob,
} from '@/api/types/request.types'
import { IJobAssigneeMandate, IJob } from '@/api/types/response.types'
import { EEntities } from '@/types'

/* API */
import WorkflowsAPI from '@/api/modules/workflows.api'

export default defineComponent({
  name: 'ManageJobsDrawer',

  components: {
    Draggable,
  },

  props: {
    workflowId: {
      type: Number,
      required: true,
    },
    jobs: {
      type: Array as () => IWorkflowsInputModifyJobsJob[],
      required: true,
    },
    assigneeMandates: {
      type: Array as () => IJobAssigneeMandate[],
      required: true,
    },
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    showDrawer: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['jobs-updated', 'update:showDrawer'],

  data() {
    return {
      TextAssistant,
      maxJobsAllowed: 10,
      isUpdatingJobs: false,
      internalJobs: [] as IWorkflowsInputModifyJobsJob[],
    }
  },

  computed: {
    visible: {
      get(): boolean {
        return this.showDrawer
      },
      set(newValue: boolean) {
        this.$emit('update:showDrawer', newValue)
      },
    },
    drawerWidthPct(): string {
      if (this.$screen.isMobile(this.$mq)) {
        return '100%'
      }
      return '680px'
    },
    btnColor(): string {
      return ColorAssistant.getEntityColor(this.entityType)
    },
    itemTypeNameSingular(): string {
      return TextAssistant.itemSingularName(this.entityType)
    },
    itemTypeNamePlural(): string {
      return TextAssistant.itemPluralName(this.entityType)
    },
    entityTypeNameSingular(): string {
      return TextAssistant.entitySingularName(this.entityType)
    },
  },

  watch: {
    jobs: {
      deep: 1,
      immediate: true,
      handler(val: IWorkflowsInputModifyJobsJob[]) {
        this.internalJobs = []
        val.forEach(j => {
          this.internalJobs.push({
            id: j.id,
            name: j.name,
          })
        })
      },
    },
  },

  created() {},

  methods: {
    /* New jobs have no ID, so the draggable list keys them by position */
    positionKey(job: IWorkflowsInputModifyJobsJob): number {
      return this.internalJobs.indexOf(job)
    },

    hasAssigneeMandate(job: IWorkflowsInputModifyJobsJob): boolean {
      return this.assigneeMandates.some(am => am.jobID === job.id)
    },
    removeJob(index: number) {
      this.internalJobs.splice(index, 1)
    },
    addAdditionalJob() {
      this.internalJobs.push({
        id: null,
        name: '',
      })
    },
    async updateJobs() {
      try {
        this.isUpdatingJobs = true
        const data = {
          listJobs: this.internalJobs,
        } as IWorkflowsInputModifyJobs

        const response = await WorkflowsAPI.put_ModifyJobs(
          this.workflowId,
          data,
        )
        this.$emit('jobs-updated')
        this.visible = false
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isUpdatingJobs = false
      }
    },
  },
})
</script>

<style lang="scss">
.manage-jobs-drawer {
  &__header {
    margin: 10px 0 0 20px;

    > h4 {
      font-size: 26px !important;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    padding-top: 20px;
    width: 100%;
  }
}
</style>
